import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileText, MessageCircle, StickyNote } from 'lucide-react';
import { api } from '../../lib/api';
import { relativeTime } from '../../lib/time';
import { Avatar, Spinner } from '@vatman/ui';
import { highlightRuns, markSubstring, searchLink, type SearchResult } from './searchText';
import './search.css';

export function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const tm = setTimeout(() => setV(value), ms);
    return () => clearTimeout(tm);
  }, [value, ms]);
  return v;
}

export function Highlighted({ text }: { text: string }) {
  return (
    <>
      {highlightRuns(text).map((r, i) =>
        r.hit ? (
          <mark key={i} className="hl">
            {r.text}
          </mark>
        ) : (
          <span key={i}>{r.text}</span>
        ),
      )}
    </>
  );
}

const SOURCE_ICON: Record<NonNullable<SearchResult['source']>, ReactNode> = {
  items: <StickyNote size={14} />,
  comments: <MessageCircle size={14} />,
  content: <FileText size={14} />,
};

export function SearchResults({ q, org, team, project }: { q: string; org: string | null; team: string | null; project: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useQuery({
    queryKey: ['search', { q, org, team, project }],
    queryFn: () => {
      const s = new URLSearchParams({ q });
      if (project) s.set('project', project);
      else if (team) s.set('team', team);
      else if (org) s.set('org', org);
      return api.get<{ results: SearchResult[] }>(`/search?${s}`).then((r) => r.results);
    },
    enabled: q.length > 0,
    placeholderData: (prev) => prev,
    staleTime: 15_000,
  });
  const results = search.data;

  if (!results) {
    return (
      <div className="empty-state">
        <Spinner />
      </div>
    );
  }
  if (results.length === 0) {
    return (
      <div className="empty-state">
        <h3>{t('search.noResults')}</h3>
        <div>{t('search.noResultsHint')}</div>
      </div>
    );
  }
  return (
    <>
      <div className="section-head">
        <h3>{t('search.titleAndContent', { count: results.length })}</h3>
        {search.isFetching && <span className="search-busy" aria-hidden="true" />}
      </div>
      <div className="search-results" data-stale={search.isPlaceholderData}>
        {results.map((r) => {
          const link = searchLink(r);
          const title = r.title ?? (r.titleMatch ? markSubstring(r.board.title, q) : r.board.title);
          const extra = Math.max(0, (r.source === 'comments' ? r.threads.length : r.items.length) - 1);
          return (
            <a
              key={r.board.id}
              className="search-row"
              href={link}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                navigate(link);
              }}
            >
              <div className="mini">{r.board.thumbnail && <img src={r.board.thumbnail} alt="" loading="lazy" />}</div>
              <div className="search-main">
                <div className="card-title">
                  <Highlighted text={title} />
                </div>
                {r.snippet && r.source && (
                  <div className="search-snippet" title={r.source === 'comments' ? t('search.inComments') : t('search.inItems')}>
                    {SOURCE_ICON[r.source]}
                    <span>
                      <Highlighted text={r.snippet} />
                    </span>
                  </div>
                )}
                <div className="card-meta">
                  {[r.board.teamName, t('dashboard.edited', { time: relativeTime(r.board.updatedAt) }), extra > 0 ? t('search.moreItems', { count: extra }) : null]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
              {r.board.owner && <Avatar name={r.board.owner.name} color={r.board.owner.color} size={24} title={t('dashboard.owner', { name: r.board.owner.name })} />}
            </a>
          );
        })}
      </div>
    </>
  );
}
